/**
 * Minimal structural types for the Recharts payload objects handed to custom
 * legend and tooltip renderers.
 *
 * Recharts does not export usable prop types for custom renderers, so these
 * describe only the fields this application actually reads. They exist so that
 * chart components can avoid `any` while staying tolerant of extra fields
 * Recharts adds.
 */

/** One series entry as passed to a custom <Legend content={...}> renderer. */
export type ChartDataKey = string | number | ((obj: never) => unknown);

export type ChartLegendEntry = {
  dataKey?: ChartDataKey;
  value?: unknown;
  color?: string;
  type?: string;
  payload?: unknown;
};

export type ChartLegendRenderProps = {
  payload?: readonly ChartLegendEntry[];
  verticalAlign?: "top" | "middle" | "bottom";
};

/** One series entry as passed to a custom <Tooltip content={...}> renderer. */
export type ChartTooltipEntry = {
  dataKey?: ChartDataKey;
  name?: unknown;
  color?: string;
  value?: unknown;
  payload?: unknown;
};

/** Coerce a Recharts payload `value` into a finite number for formatting. */
export function toNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

/** Stable string key for a payload entry, used to de-duplicate series. */
export function entryKey(entry: { dataKey?: ChartDataKey; value?: unknown }): string {
  const k = entry.dataKey;
  if (typeof k === "string" || typeof k === "number") return String(k);
  return typeof entry.value === "string" || typeof entry.value === "number"
    ? String(entry.value)
    : "";
}

export type ChartTooltipRenderProps = {
  active?: boolean;
  payload?: readonly ChartTooltipEntry[];
  label?: string | number;
};

/** Narrow an unknown chart datum field to a finite number, defaulting to 0. */
export function numericField(row: unknown, key: string): number {
  if (typeof row !== "object" || row === null) return 0;
  const v = (row as Record<string, unknown>)[key];
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}
